import React from 'react';
import type { ConditionFieldOption, ConditionTree } from '../../../models/MasterAdmin/pushNotificationCampaign';

type Props = {
  value: ConditionTree | null;
  fields: ConditionFieldOption[];
  onChange: (tree: ConditionTree | null) => void;
  disabled?: boolean;
};

const emptyLeaf = (field?: string): ConditionTree => ({
  field: field || '',
  operator: 'equals',
  value: '',
});

const emptyGroup = (): ConditionTree => ({
  combinator: 'AND',
  rules: [emptyLeaf()],
});

function isGroup(node: ConditionTree): boolean {
  return Array.isArray(node.rules);
}

const ConditionNode: React.FC<{
  node: ConditionTree;
  fields: ConditionFieldOption[];
  onChange: (node: ConditionTree) => void;
  onRemove?: () => void;
  disabled?: boolean;
  depth: number;
}> = ({ node, fields, onChange, onRemove, disabled, depth }) => {
  if (isGroup(node)) {
    return (
      <div className={`space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-600 ${depth > 0 ? 'ml-2' : ''}`}>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            value={node.combinator || 'AND'}
            disabled={disabled}
            onChange={(e) => onChange({ ...node, combinator: e.target.value as 'AND' | 'OR' })}
          >
            <option value="AND">AND</option>
            <option value="OR">OR</option>
          </select>
          {onRemove && (
            <button type="button" className="text-xs text-red-600" disabled={disabled} onClick={onRemove}>
              Remove group
            </button>
          )}
        </div>
        {(node.rules || []).map((child, i) => (
          <ConditionNode
            key={i}
            node={child}
            fields={fields}
            depth={depth + 1}
            disabled={disabled}
            onChange={(next) => {
              const rules = [...(node.rules || [])];
              rules[i] = next;
              onChange({ ...node, rules });
            }}
            onRemove={() => {
              const rules = (node.rules || []).filter((_, idx) => idx !== i);
              onChange({ ...node, rules: rules.length ? rules : [emptyLeaf(fields[0]?.field)] });
            }}
          />
        ))}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled}
            className="text-xs font-medium text-blue-600"
            onClick={() => onChange({ ...node, rules: [...(node.rules || []), emptyLeaf(fields[0]?.field)] })}
          >
            + Add condition
          </button>
          {depth < 2 && (
            <button
              type="button"
              disabled={disabled}
              className="text-xs font-medium text-blue-600"
              onClick={() => onChange({ ...node, rules: [...(node.rules || []), emptyGroup()] })}
            >
              + Add group
            </button>
          )}
        </div>
      </div>
    );
  }

  const spec = fields.find((f) => f.field === node.field);
  const operators = spec?.operators || ['equals'];
  const valueType = spec?.valueType || 'string';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        value={node.field || ''}
        disabled={disabled}
        onChange={(e) => {
          const next = fields.find((f) => f.field === e.target.value);
          onChange({
            field: e.target.value,
            operator: next?.operators[0] || 'equals',
            value: '',
          });
        }}
      >
        <option value="">Select field</option>
        {fields.map((f) => (
          <option key={f.field} value={f.field}>
            {f.label}
          </option>
        ))}
      </select>
      <select
        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        value={node.operator || 'equals'}
        disabled={disabled}
        onChange={(e) => onChange({ ...node, operator: e.target.value })}
      >
        {operators.map((op) => (
          <option key={op} value={op}>
            {op.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
      {node.operator !== 'exists' && node.operator !== 'not_exists' && (
        <input
          className="w-36 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          value={String(node.value ?? '')}
          disabled={disabled}
          type={valueType === 'number' || node.operator?.includes('days') ? 'number' : 'text'}
          placeholder="Value"
          onChange={(e) => onChange({ ...node, value: e.target.value })}
        />
      )}
      {onRemove && (
        <button type="button" className="text-xs text-red-600" disabled={disabled} onClick={onRemove}>
          Remove
        </button>
      )}
    </div>
  );
};

const ConditionBuilder: React.FC<Props> = ({ value, fields, onChange, disabled }) => {
  const tree = value || emptyGroup();
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-900 dark:text-white">Conditions</p>
        {value && (
          <button type="button" className="text-xs text-gray-500" disabled={disabled} onClick={() => onChange(null)}>
            Clear all
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Applied after the audience. Fields come from the server allowlist — unsupported fields are rejected.
      </p>
      <ConditionNode
        node={tree}
        fields={fields}
        depth={0}
        disabled={disabled}
        onChange={(next) => onChange(next)}
      />
    </div>
  );
};

export default ConditionBuilder;
